import DestructibleType from "../enums/DestructibleType";
import EnemyType from "../enums/EnemyType";
import StageEventType from "../enums/StageEventType";
import StageType from "../enums/StageType";
import { FormationConfig } from "../game/formations/BaseFormation";

// Local copies of stage types (kept here to avoid circular imports)
export interface StageEvent {
  eventType: StageEventType;
  delay: number;
  repeat: number;
  // The smaller the value, the easier it is to refresh
  chance?: any;
  duration?: number;
}

export interface StageData {
  stageNameKey?: string;
  descriptionKey?: string;
  imageKey?: string;
  unlocked?: boolean;
  hidden?: boolean;
  startingSpawns?: number;
  minute?: number; // Can be 0, 0.5, 1, 1.5, etc.
  destructibleType?: DestructibleType;
  destructibleFreq?: number;
  destructibleChance?: number;
  destructibleChanceMax?: number;
  maxDestructibles?: number;
  minimum?: number;
  frequency?: number;
  bosses?: EnemyType[];
  enemies?: EnemyType[];
  events?: StageEvent[];
  formations?: FormationConfig[];
}

const STAGES_GREEN: Partial<Record<StageType, StageData[]>> = {
  // GREEN1 mirrors DESERT2
  [StageType.GREEN1]: [
    {
      stageNameKey: "stages.green1.name",
      descriptionKey: "stages.green1.description",
      imageKey: "green_background",
      unlocked: true,
      hidden: false,
      startingSpawns: 1,
      minute: 0,
      destructibleType: DestructibleType.GIFT,
      destructibleFreq: 1000,
      destructibleChance: 10,
      destructibleChanceMax: 50,
      maxDestructibles: 20,
      minimum: 20,
      frequency: 250,
      enemies: [EnemyType.CHICKEN],
      formations: [
        {
          enemyType: EnemyType.CHICKEN,
          enemyCount: 27,
          radius: 250,
          rotationSpeed: 0.01,
          spawnDistance: 2000,
          showPointerAfterMinute: 0,
        },
      ],
      events: [],
    },
    {
      minute: 0.5,
      minimum: 15,
      frequency: 50,
      enemies: [EnemyType.CHICKEN],
      events: [],
    },
    {
      minute: 1,
      minimum: 20,
      frequency: 100,
      enemies: [EnemyType.CHICKEN, EnemyType.DUCK],
      bosses: [],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 4000,
          repeat: 5,
          chance: 80,
        },
      ],
    },
    {
      minute: 1.5,
      minimum: 10,
      frequency: 200,
      enemies: [EnemyType.CHICKEN, EnemyType.DUCK],
      bosses: [],
      events: [],
    },
    {
      minute: 2,
      minimum: 10,
      frequency: 200,
      enemies: [EnemyType.CHICKEN, EnemyType.DUCK],
      bosses: [EnemyType.TIGER],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 60,
        },
      ],
    },
    {
      minute: 2.5,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.DUCK],
      events: [],
    },
    {
      minute: 3,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.DUCK],
      bosses: [],
      events: [],
    },
    {
      minute: 3.5,
      minimum: 30,
      frequency: 200,
      enemies: [EnemyType.TREANT, EnemyType.DUCK],
      bosses: [],
      events: [],
    },
    {
      minute: 4,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.MOUSEMAN, EnemyType.SPIDER],
      bosses: [],
      events: [],
    },
    {
      minute: 4.5,
      minimum: 20,
      frequency: 100,
      enemies: [EnemyType.TREANT],
      bosses: [],
      events: [],
    },
    {
      minute: 5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.MOUSEMAN, EnemyType.TREANT],
      bosses: [EnemyType.DEATH],
      events: [],
    },
  ],

  // GREEN2 mirrors DESERT3
  [StageType.GREEN2]: [
    {
      stageNameKey: "stages.green2.name",
      descriptionKey: "stages.green2.description",
      imageKey: "green_background",
      unlocked: true,
      hidden: false,
      startingSpawns: 1,
      minute: 0,
      destructibleType: DestructibleType.GIFT,
      destructibleFreq: 1000,
      destructibleChance: 10,
      destructibleChanceMax: 50,
      maxDestructibles: 20,
      minimum: 10,
      frequency: 50,
      enemies: [EnemyType.CHICKEN],
      formations: [
        {
          enemyType: EnemyType.MOUSEMAN,
          enemyCount: 27,
          radius: 250,
          rotationSpeed: 0.01,
          spawnDistance: 10000,
          showPointerAfterMinute: 0,
        },
      ],
      events: [],
    },
    {
      minute: 0.5,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.CHICKEN, EnemyType.DUCK],
      events: [],
    },
    {
      minute: 1,
      minimum: 10,
      frequency: 250,
      enemies: [EnemyType.CHICKEN, EnemyType.DUCK, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 1.5,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.DUCK, EnemyType.CHICKEN],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 4000,
          repeat: 5,
          chance: 80,
        },
      ],
    },
    {
      minute: 2,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.CHICKEN, EnemyType.DUCK],
      bosses: [EnemyType.CROCODILE],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 60,
        },
      ],
    },
    {
      minute: 2.5,
      minimum: 15,
      frequency: 150,
      enemies: [EnemyType.DUCK, EnemyType.MOUSEMAN],
      events: [],
    },
    {
      minute: 3,
      minimum: 15,
      frequency: 100,
      enemies: [EnemyType.MOUSEMAN, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 3.5,
      minimum: 20,
      frequency: 100,
      enemies: [EnemyType.MOUSEMAN, EnemyType.TREANT],
      events: [],
    },
    {
      minute: 4,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.MOUSEMAN, EnemyType.TREANT],
      bosses: [],
      events: [],
    },
    {
      minute: 4.5,
      minimum: 20,
      frequency: 100,
      enemies: [EnemyType.TREANT],
      bosses: [EnemyType.TIGER],
      events: [],
    },
    {
      minute: 5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.TREANT, EnemyType.DUCK],
      bosses: [],
      events: [],
    },
    {
      minute: 5.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.TREANT, EnemyType.DUCK],
      bosses: [],
      events: [],
    },
    {
      minute: 6,
      minimum: 25,
      frequency: 50,
      enemies: [EnemyType.TREANT, EnemyType.DUCK, EnemyType.SPIDER],
      bosses: [EnemyType.TIGER],
      events: [],
    },
    {
      minute: 6.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.TREANT],
      bosses: [],
      events: [],
    },
    {
      minute: 7,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.TREANT],
      bosses: [],
      events: [],
    },
    {
      minute: 7.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.TREANT],
      bosses: [],
      events: [],
    },
    {
      minute: 8,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.TREANT, EnemyType.MOUSEMAN2],
      bosses: [],
      events: [],
    },
    {
      minute: 8.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.TREANT, EnemyType.MOUSEMAN2],
      bosses: [],
      events: [],
    },
    {
      minute: 9,
      minimum: 10,
      frequency: 100,
      enemies: [EnemyType.DUCK],
      bosses: [EnemyType.DEATH],
      events: [],
    },
  ],

  // GREEN3 mirrors DESERT4
  [StageType.GREEN3]: [
    {
      stageNameKey: "stages.green3.name",
      descriptionKey: "stages.green3.description",
      imageKey: "green_background",
      unlocked: true,
      hidden: false,
      startingSpawns: 3,
      minute: 0,
      destructibleType: DestructibleType.GIFT,
      destructibleFreq: 1000,
      destructibleChance: 10,
      destructibleChanceMax: 50,
      maxDestructibles: 20,
      minimum: 10,
      frequency: 100,
      enemies: [EnemyType.DUCK],
      formations: [
        {
          enemyType: EnemyType.DUCK,
          enemyCount: 27,
          radius: 250,
          rotationSpeed: 0.01,
          spawnDistance: 1000,
          showPointerAfterMinute: 0,
        },
        {
          enemyType: EnemyType.MOUSEMAN,
          enemyCount: 30,
          radius: 250,
          rotationSpeed: 0.01,
          spawnDistance: 10000,
          showPointerAfterMinute: 2,
        },
      ],
      events: [],
    },
    {
      minute: 0.5,
      minimum: 15,
      frequency: 100,
      enemies: [EnemyType.MOUSEMAN, EnemyType.DUCK],
      events: [],
    },
    {
      minute: 1,
      minimum: 15,
      frequency: 100,
      enemies: [EnemyType.CHICKEN, EnemyType.DUCK],
      bosses: [EnemyType.TIGER],
      events: [],
    },
    {
      minute: 1.5,
      minimum: 15,
      frequency: 50,
      enemies: [EnemyType.MOUSEMAN],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 2,
      minimum: 10,
      frequency: 100,
      enemies: [EnemyType.SPIDER, EnemyType.DUCK],
      events: [],
    },
    {
      minute: 2.5,
      minimum: 10,
      frequency: 50,
      enemies: [EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 3,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 3.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.SPIDER, EnemyType.DUCK],
      bosses: [EnemyType.TIGER],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 4,
      minimum: 40,
      frequency: 100,
      enemies: [EnemyType.MOUSEMAN, EnemyType.MOUSEMAN2, EnemyType.DUCK],
      events: [],
    },
    {
      minute: 4.5,
      minimum: 70,
      frequency: 100,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.MOUSEMAN],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 5,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.TREANT, EnemyType.MOUSEMAN],
      bosses: [],
      events: [],
    },
    {
      minute: 5.5,
      minimum: 20,
      frequency: 30,
      enemies: [EnemyType.TREANT],
      events: [],
    },
    {
      minute: 6,
      minimum: 30,
      frequency: 30,
      enemies: [EnemyType.TREANT],
      bosses: [],
      events: [],
    },
    {
      minute: 6.5,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.TREANT, EnemyType.SPIDER],
      bosses: [EnemyType.TIGER],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 7,
      minimum: 15,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.DUCK],
      events: [],
    },
    {
      minute: 7.5,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.DUCK],
      events: [],
    },
    {
      minute: 8,
      minimum: 40,
      frequency: 100,
      enemies: [EnemyType.BEE],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 8.5,
      minimum: 40,
      frequency: 100,
      enemies: [EnemyType.BEE],
      bosses: [EnemyType.RHINOCEROS],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 9,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.MOUSEMAN2],
      events: [],
    },
    {
      minute: 9.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.MOUSEMAN2],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 10,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.MOUSEMAN2],
      bosses: [EnemyType.DEATH],
      events: [],
    },
  ],

  // GREEN4 mirrors DESERT5
  [StageType.GREEN4]: [
    {
      stageNameKey: "stages.green4.name",
      descriptionKey: "stages.green4.description",
      imageKey: "green_background",
      unlocked: true,
      hidden: false,
      startingSpawns: 3,
      minute: 0,
      destructibleType: DestructibleType.GIFT,
      destructibleFreq: 1000,
      destructibleChance: 10,
      destructibleChanceMax: 50,
      maxDestructibles: 20,
      minimum: 10,
      frequency: 200,
      enemies: [EnemyType.SPIDER, EnemyType.CHICKEN],
      formations: [
        {
          enemyType: EnemyType.SPIDER,
          enemyCount: 27,
          radius: 250,
          rotationSpeed: 0.01,
          spawnDistance: 6000,
          showPointerAfterMinute: 0,
        },
        {
          enemyType: EnemyType.MOUSEMAN2,
          enemyCount: 30,
          radius: 250,
          rotationSpeed: 0.01,
          spawnDistance: 10000,
          showPointerAfterMinute: 2,
        },
      ],
      events: [],
    },
    {
      minute: 0.5,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.DUCK],
      events: [],
    },
    {
      minute: 1,
      minimum: 10,
      frequency: 50,
      enemies: [EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 1.5,
      minimum: 20,
      frequency: 100,
      enemies: [EnemyType.DUCK, EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 2,
      minimum: 30,
      frequency: 20,
      enemies: [EnemyType.SPIDER, EnemyType.DUCK],
      events: [],
    },
    {
      minute: 2.5,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.SPIDER, EnemyType.MOUSEMAN],
      events: [],
    },
    {
      minute: 3,
      minimum: 40,
      frequency: 100,
      enemies: [EnemyType.MOUSEMAN, EnemyType.SPIDER, EnemyType.DUCK],
      bosses: [EnemyType.CROCODILE],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 3.5,
      minimum: 20,
      frequency: 100,
      enemies: [EnemyType.DUCK, EnemyType.MOUSEMAN2],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 4,
      minimum: 30,
      frequency: 150,
      enemies: [EnemyType.SPIDER, EnemyType.MOUSEMAN2, EnemyType.DUCK],
      events: [],
    },
    {
      minute: 4.5,
      minimum: 70,
      frequency: 100,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 5,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.SPIDER],
      bosses: [EnemyType.TIGER],
      events: [],
    },
    {
      minute: 5.5,
      minimum: 70,
      frequency: 30,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 6,
      minimum: 70,
      frequency: 30,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.SPIDER],
      bosses: [],
      events: [],
    },
    {
      minute: 6.5,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 7,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 7.5,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.SPIDER],
      bosses: [EnemyType.TIGER, EnemyType.CROCODILE],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 8,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.BEE],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 8.5,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SPIDER],
      bosses: [],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 9,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 9.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 10,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SPIDER],
      bosses: [EnemyType.DEATH],
      events: [],
    },
  ],

  // GREEN5 mirrors DESERT6
  [StageType.GREEN5]: [
    {
      stageNameKey: "stages.green5.name",
      descriptionKey: "stages.green5.description",
      imageKey: "green_background",
      unlocked: true,
      hidden: false,
      startingSpawns: 3,
      minute: 0,
      destructibleType: DestructibleType.GIFT,
      destructibleFreq: 1000,
      destructibleChance: 10,
      destructibleChanceMax: 50,
      maxDestructibles: 20,
      minimum: 40,
      frequency: 200,
      enemies: [EnemyType.CHICKEN],
      formations: [
        {
          enemyType: EnemyType.MOUSEMAN2,
          enemyCount: 27,
          radius: 250,
          rotationSpeed: 0.01,
          spawnDistance: 12000,
          showPointerAfterMinute: 0,
        },
      ],
      events: [],
    },
    {
      minute: 0.5,
      minimum: 20,
      frequency: 250,
      enemies: [EnemyType.DUCK],
      bosses: [EnemyType.RHINOCEROS],
      events: [],
    },
    {
      minute: 1,
      minimum: 20,
      frequency: 250,
      enemies: [EnemyType.DUCK],
      events: [],
    },
    {
      minute: 1.5,
      minimum: 20,
      frequency: 200,
      enemies: [EnemyType.DUCK],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 2,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.SPIDER, EnemyType.CHICKEN],
      events: [],
    },
    {
      minute: 2.5,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.DUCK],
      events: [],
    },
    {
      minute: 3,
      minimum: 40,
      frequency: 100,
      enemies: [EnemyType.MOUSEMAN, EnemyType.DUCK],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 3.5,
      minimum: 60,
      frequency: 100,
      enemies: [EnemyType.DUCK, EnemyType.MOUSEMAN],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 4,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.DUCK],
      bosses: [EnemyType.TIGER, EnemyType.TIGER],
      events: [],
    },
    {
      minute: 4.5,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.MOUSEMAN],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 5,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.SPIDER],
      bosses: [],
      events: [],
    },
    {
      minute: 5.5,
      minimum: 70,
      frequency: 30,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 6,
      minimum: 70,
      frequency: 30,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.BEE],
      bosses: [],
      events: [],
    },
    {
      minute: 6.5,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.MOUSEMAN2],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 7,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.MOUSEMAN2],
      events: [],
    },
    {
      minute: 7.5,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.MOUSEMAN2],
      bosses: [EnemyType.CROCODILE],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 8,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.BEE],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 8.5,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.TREANT],
      bosses: [],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 9,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.TREANT],
      events: [],
    },
    {
      minute: 9.5,
      minimum: 40,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 10,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM],
      bosses: [],
      events: [],
    },
    {
      minute: 10.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM],
      bosses: [],
      events: [],
    },
    {
      minute: 11,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM],
      bosses: [],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 2000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 11.5,
      minimum: 70,
      frequency: 20,
      enemies: [EnemyType.GREEN_GOLEM],
      bosses: [],
      events: [],
    },
    {
      minute: 12,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM],
      bosses: [EnemyType.DEATH],
      events: [],
    },
  ],

  // GREEN6 mirrors DESERT7
  [StageType.GREEN6]: [
    {
      stageNameKey: "stages.green6.name",
      descriptionKey: "stages.green6.description",
      imageKey: "green_background",
      unlocked: true,
      hidden: false,
      startingSpawns: 3,
      minute: 0,
      destructibleType: DestructibleType.GIFT,
      destructibleFreq: 1000,
      destructibleChance: 10,
      destructibleChanceMax: 50,
      maxDestructibles: 20,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.DUCK],
      formations: [
        {
          enemyType: EnemyType.TREANT,
          enemyCount: 12,
          radius: 150,
          rotationSpeed: 0.01,
          spawnDistance: 2000,
          showPointerAfterMinute: 1,
        },
        {
          enemyType: EnemyType.MOUSEMAN2,
          enemyCount: 30,
          radius: 250,
          rotationSpeed: 0.01,
          spawnDistance: 8000,
          showPointerAfterMinute: 2,
        },
      ],
      events: [],
    },
    {
      minute: 0.5,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.MOUSEMAN, EnemyType.CHICKEN],
      events: [],
    },
    {
      minute: 1,
      minimum: 20,
      frequency: 100,
      enemies: [EnemyType.DUCK, EnemyType.MOUSEMAN, EnemyType.TREANT],
      events: [],
    },
    {
      minute: 1.5,
      minimum: 40,
      frequency: 150,
      enemies: [EnemyType.TREANT],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 2,
      minimum: 40,
      frequency: 100,
      enemies: [EnemyType.TREANT, EnemyType.MOUSEMAN],
      events: [],
    },
    {
      minute: 2.5,
      minimum: 70,
      frequency: 20,
      enemies: [EnemyType.MOUSEMAN],
      events: [],
    },
    {
      minute: 3,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.TREANT],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 3.5,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.TREANT],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 4,
      minimum: 60,
      frequency: 100,
      enemies: [EnemyType.MOUSEMAN, EnemyType.TREANT],
      events: [],
      bosses: [EnemyType.LION],
    },
    {
      minute: 4.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.TREANT],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 5,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.MOUSEMAN, EnemyType.TREANT],
      bosses: [],
      events: [],
    },
    {
      minute: 5.5,
      minimum: 70,
      frequency: 30,
      enemies: [
        EnemyType.MOUSEMAN2,
        EnemyType.MOUSEMAN,
        EnemyType.TREANT,
        EnemyType.SPIDER,
      ],
      events: [],
    },
    {
      minute: 6,
      minimum: 70,
      frequency: 30,
      enemies: [
        EnemyType.MOUSEMAN2,
        EnemyType.MOUSEMAN,
        EnemyType.TREANT,
        EnemyType.SPIDER,
      ],
      bosses: [],
      events: [],
    },
    {
      minute: 6.5,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.TREANT],
      bosses: [EnemyType.CROCODILE, EnemyType.TIGER],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 7,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.TREANT],
      events: [],
    },
    {
      minute: 7.5,
      minimum: 50,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.TREANT, EnemyType.MOUSEMAN2],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 8,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.BEE],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 8.5,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.TREANT],
      bosses: [],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 9,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.TREANT],
      events: [],
    },
    {
      minute: 9.5,
      minimum: 70,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 10,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM],
      bosses: [],
      events: [],
    },
    {
      minute: 10.5,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SKELETON],
      bosses: [EnemyType.CROCODILE],
      events: [],
    },
    {
      minute: 11,
      minimum: 20,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SKELETON],
      bosses: [],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 2000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 11.5,
      minimum: 70,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SKELETON],
      bosses: [],
      events: [],
    },
    {
      minute: 12,
      minimum: 70,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SKELETON],
      bosses: [EnemyType.DEATH],
      events: [],
    },
  ],

  // GREEN7 mirrors DESERT8
  [StageType.GREEN7]: [
    {
      stageNameKey: "stages.green7.name",
      descriptionKey: "stages.green7.description",
      imageKey: "green_background",
      unlocked: true,
      hidden: false,
      minute: 0,
      frequency: 100,
      enemies: [EnemyType.DUCK],
      bosses: [],
      startingSpawns: 10,
      destructibleType: DestructibleType.GIFT,
      destructibleFreq: 1000,
      destructibleChance: 10,
      destructibleChanceMax: 50,
      maxDestructibles: 20,
      minimum: 30,
      formations: [
        {
          enemyType: EnemyType.GREEN_GOLEM,
          enemyCount: 17,
          radius: 200,
          rotationSpeed: 0.01,
          spawnDistance: 500,
          showPointerAfterMinute: 0,
        },
        {
          enemyType: EnemyType.BEE,
          enemyCount: 27,
          radius: 250,
          rotationSpeed: 0.01,
          spawnDistance: 8000,
          showPointerAfterMinute: 2,
        },
      ],
      events: [],
    },
    {
      minute: 0.5,
      minimum: 50,
      frequency: 100,
      enemies: [EnemyType.DUCK, EnemyType.MOUSEMAN],
      events: [],
    },
    {
      minute: 1,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.TREANT],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 1.5,
      minimum: 50,
      frequency: 50,
      enemies: [EnemyType.DUCK, EnemyType.TREANT],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 2,
      minimum: 50,
      frequency: 20,
      enemies: [EnemyType.SPIDER, EnemyType.TREANT],
      events: [],
    },
    {
      minute: 2.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.DUCK],
      bosses: [EnemyType.RHINOCEROS],
      events: [],
    },
    {
      minute: 3,
      minimum: 40,
      frequency: 20,
      enemies: [EnemyType.TREANT],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 3.5,
      minimum: 25,
      frequency: 50,
      enemies: [EnemyType.MOUSEMAN, EnemyType.TREANT],
      bosses: [EnemyType.GORILLA],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 4,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.TREANT],
      events: [],
    },
    {
      minute: 4.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.MOUSEMAN2],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 5,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.MOUSEMAN, EnemyType.TREANT],
      bosses: [],
      events: [],
    },
    {
      minute: 5.5,
      minimum: 70,
      frequency: 30,
      enemies: [
        EnemyType.MOUSEMAN2,
        EnemyType.MOUSEMAN,
        EnemyType.TREANT,
        EnemyType.SPIDER,
      ],
      events: [],
    },
    {
      minute: 6,
      minimum: 70,
      frequency: 30,
      enemies: [
        EnemyType.MOUSEMAN2,
        EnemyType.MOUSEMAN,
        EnemyType.TREANT,
        EnemyType.SPIDER,
      ],
      bosses: [],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 6.5,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.BEE],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 7,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.BEE],
      bosses: [EnemyType.CROCODILE],
      events: [],
    },
    {
      minute: 7.5,
      minimum: 50,
      frequency: 50,
      enemies: [EnemyType.BEE],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 8,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.TREANT],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 8.5,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.TREANT],
      bosses: [EnemyType.TIGER],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 9,
      minimum: 40,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM],
      events: [],
    },
    {
      minute: 9.5,
      minimum: 70,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 10,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM],
      bosses: [],
      events: [],
    },
    {
      minute: 10.5,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SKELETON],
      bosses: [],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 11,
      minimum: 20,
      frequency: 100,
      enemies: [EnemyType.SKELETON],
      bosses: [],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 2000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 11.5,
      minimum: 50,
      frequency: 100,
      enemies: [EnemyType.SKELETON],
      bosses: [],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 12,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SKELETON],
      bosses: [EnemyType.LION],
      events: [],
    },
    {
      minute: 12.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SKELETON],
      bosses: [],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 13,
      minimum: 70,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SKELETON, EnemyType.MOUSEMAN2],
      bosses: [],
      events: [],
    },
    {
      minute: 13.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.SKELETON, EnemyType.MOUSEMAN2],
      bosses: [],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 14,
      minimum: 30,
      frequency: 150,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.WOLF],
      bosses: [],
      events: [],
    },
    {
      minute: 14.5,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.WOLF],
      bosses: [],
      events: [],
    },
    {
      minute: 15,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.GREEN_GOLEM, EnemyType.WOLF],
      bosses: [EnemyType.DEATH],
      events: [],
    },
  ],

  // GREEN8 mirrors DESERT9
  [StageType.GREEN8]: [
    {
      stageNameKey: "stages.green8.name",
      descriptionKey: "stages.green8.description",
      imageKey: "green_background",
      unlocked: true,
      hidden: false,
      minute: 0,
      frequency: 100,
      enemies: [EnemyType.SPIDER],
      bosses: [],
      startingSpawns: 3,
      destructibleType: DestructibleType.GIFT,
      destructibleFreq: 1000,
      destructibleChance: 10,
      destructibleChanceMax: 50,
      maxDestructibles: 30,
      minimum: 30,
      formations: [
        {
          enemyType: EnemyType.WOLF,
          enemyCount: 30,
          radius: 200,
          rotationSpeed: 0.01,
          spawnDistance: 10000,
          showPointerAfterMinute: 2.5,
        },
      ],
      events: [],
    },
    {
      minute: 0.5,
      minimum: 50,
      frequency: 50,
      enemies: [EnemyType.DUCK, EnemyType.TREANT],
      events: [],
    },
    {
      minute: 1,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.TREANT],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 1.5,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.MOUSEMAN2, EnemyType.TREANT, EnemyType.GREEN_GOLEM],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 2,
      minimum: 50,
      frequency: 20,
      enemies: [EnemyType.SPIDER, EnemyType.MOUSEMAN],
      bosses: [
        EnemyType.RHINOCEROS,
        EnemyType.RHINOCEROS,
        EnemyType.RHINOCEROS,
      ],
      events: [],
    },
    {
      minute: 2.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 3,
      minimum: 50,
      frequency: 100,
      enemies: [EnemyType.DUCK, EnemyType.SKELETON],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 3.5,
      minimum: 20,
      frequency: 100,
      enemies: [EnemyType.SKELETON],
      bosses: [],
      events: [],
    },
    {
      minute: 4,
      minimum: 20,
      frequency: 100,
      enemies: [EnemyType.GREEN_GOLEM],
      events: [],
    },
    {
      minute: 4.5,
      minimum: 70,
      frequency: 100,
      enemies: [EnemyType.BEE],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 5,
      minimum: 60,
      frequency: 500,
      enemies: [EnemyType.TREANT, EnemyType.BEE, EnemyType.MOUSEMAN2],
      bosses: [],
      events: [],
    },
    {
      minute: 5.5,
      minimum: 50,
      frequency: 100,
      enemies: [EnemyType.TREANT, EnemyType.BEE, EnemyType.MOUSEMAN2],
      events: [],
    },
    {
      minute: 6,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.MOUSEMAN2, EnemyType.GREEN_GOLEM],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 6.5,
      minimum: 50,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.MOUSEMAN2, EnemyType.GREEN_GOLEM],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 7,
      minimum: 50,
      frequency: 20,
      enemies: [
        EnemyType.BEE,
        EnemyType.MOUSEMAN2,
        EnemyType.GREEN_GOLEM,
        EnemyType.SPIDER,
      ],
      events: [],
    },
    {
      minute: 7.5,
      minimum: 70,
      frequency: 50,
      enemies: [
        EnemyType.BEE,
        EnemyType.MOUSEMAN2,
        EnemyType.GREEN_GOLEM,
        EnemyType.SPIDER,
      ],
      bosses: [],
      events: [],
    },
    {
      minute: 8,
      minimum: 40,
      frequency: 20,
      enemies: [EnemyType.BEE, EnemyType.GREEN_GOLEM],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 8.5,
      minimum: 25,
      frequency: 50,
      enemies: [EnemyType.SKELETON, EnemyType.DUCK],
      bosses: [EnemyType.DARK_KNIGHT],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 9,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.SKELETON, EnemyType.DUCK],
      events: [],
    },
    {
      minute: 9.5,
      minimum: 40,
      frequency: 50,
      enemies: [EnemyType.SKELETON],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 10,
      minimum: 60,
      frequency: 50,
      enemies: [EnemyType.SKELETON],
      bosses: [],
      events: [],
    },
    {
      minute: 10.5,
      minimum: 50,
      frequency: 100,
      enemies: [EnemyType.SKELETON, EnemyType.BEE],
      events: [],
    },
    {
      minute: 11,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.SKELETON, EnemyType.SPIDER, EnemyType.BEE],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 11.5,
      minimum: 50,
      frequency: 50,
      enemies: [EnemyType.SKELETON, EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 12,
      minimum: 50,
      frequency: 20,
      enemies: [EnemyType.SKELETON, EnemyType.SPIDER, EnemyType.BEE],
      events: [],
    },
    {
      minute: 12.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.SKELETON, EnemyType.BEE],
      bosses: [],
      events: [],
    },
    {
      minute: 13,
      minimum: 40,
      frequency: 20,
      enemies: [EnemyType.SKELETON, EnemyType.BEE],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 13.5,
      minimum: 25,
      frequency: 50,
      enemies: [EnemyType.SKELETON, EnemyType.GREEN_GOLEM],
      bosses: [],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 14,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.WOLF, EnemyType.BEE, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 14.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.WOLF, EnemyType.BEE, EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 15,
      minimum: 60,
      frequency: 100,
      enemies: [EnemyType.WOLF, EnemyType.BEE, EnemyType.SPIDER],
      bosses: [EnemyType.DEATH],
      events: [],
    },
  ],

  // GREEN9 mirrors DESERT10 (boss rush)
  [StageType.GREEN9]: [
    {
      stageNameKey: "stages.green9.name",
      descriptionKey: "stages.green9.description",
      imageKey: "green_background",
      unlocked: true,
      hidden: false,
      startingSpawns: 1,
      minute: 0,
      destructibleType: DestructibleType.GIFT,
      destructibleFreq: 1000,
      destructibleChance: 10,
      destructibleChanceMax: 50,
      maxDestructibles: 20,
      minimum: 10,
      frequency: 250,
      enemies: [EnemyType.DUCK],
      formations: [],
      events: [],
    },
    {
      minute: 0.5,
      minimum: 10,
      frequency: 100,
      enemies: [],
      bosses: [EnemyType.TIGER, EnemyType.TIGER],
      events: [],
    },
    {
      minute: 1,
      minimum: 10,
      frequency: 100,
      enemies: [],
      bosses: [],
      events: [],
    },
    {
      minute: 1.5,
      minimum: 20,
      frequency: 150,
      enemies: [],
      bosses: [EnemyType.RHINOCEROS, EnemyType.RHINOCEROS],
      events: [],
    },
    {
      minute: 2,
      minimum: 20,
      frequency: 150,
      enemies: [],
      bosses: [],
      events: [],
    },
    {
      minute: 2.5,
      minimum: 15,
      frequency: 150,
      enemies: [],
      bosses: [EnemyType.CROCODILE, EnemyType.CROCODILE],
      events: [],
    },
    {
      minute: 3,
      minimum: 15,
      frequency: 100,
      enemies: [],
      events: [],
    },
    {
      minute: 3.5,
      minimum: 20,
      frequency: 100,
      enemies: [],
      bosses: [EnemyType.LION, EnemyType.GORILLA],
      events: [],
    },
    {
      minute: 4,
      minimum: 10,
      frequency: 500,
      enemies: [],
      bosses: [],
      events: [],
    },
    {
      minute: 4.5,
      minimum: 10,
      frequency: 500,
      enemies: [],
      bosses: [],
      events: [],
    },
    {
      minute: 5,
      minimum: 10,
      frequency: 500,
      enemies: [],
      events: [],
      bosses: [EnemyType.DEATH],
    },
  ],

  // GREEN10 mirrors DESERT11 (long)
  [StageType.GREEN10]: [
    {
      stageNameKey: "stages.green10.name",
      descriptionKey: "stages.green10.description",
      imageKey: "green_background",
      unlocked: true,
      hidden: false,
      minute: 0,
      frequency: 100,
      enemies: [EnemyType.CHICKEN],
      bosses: [],
      startingSpawns: 10,
      destructibleType: DestructibleType.GIFT,
      destructibleFreq: 1000,
      destructibleChance: 10,
      destructibleChanceMax: 50,
      maxDestructibles: 20,
      minimum: 30,
      formations: [],
      events: [],
    },
    {
      minute: 0.5,
      minimum: 50,
      frequency: 100,
      enemies: [EnemyType.DUCK],
      events: [],
    },
    {
      minute: 1,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.DUCK],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 1.5,
      minimum: 50,
      frequency: 50,
      enemies: [EnemyType.DUCK, EnemyType.MOUSEMAN],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 2,
      minimum: 50,
      frequency: 20,
      enemies: [EnemyType.MOUSEMAN],
      events: [],
    },
    {
      minute: 2.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.MOUSEMAN],
      bosses: [],
      events: [],
    },
    {
      minute: 3,
      minimum: 40,
      frequency: 20,
      enemies: [EnemyType.TREANT, EnemyType.MOUSEMAN],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 3.5,
      minimum: 25,
      frequency: 50,
      enemies: [EnemyType.TREANT, EnemyType.SPIDER],
      bosses: [],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 4,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.TREANT, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 4.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.TREANT, EnemyType.BEE, EnemyType.MOUSEMAN2],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 5,
      minimum: 60,
      frequency: 500,
      enemies: [EnemyType.TREANT, EnemyType.BEE, EnemyType.MOUSEMAN2],
      bosses: [],
      events: [],
    },
    {
      minute: 5.5,
      minimum: 50,
      frequency: 100,
      enemies: [EnemyType.TREANT, EnemyType.BEE, EnemyType.MOUSEMAN2],
      events: [],
    },
    {
      minute: 6,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.MOUSEMAN2, EnemyType.GREEN_GOLEM],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 6.5,
      minimum: 50,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.MOUSEMAN2, EnemyType.GREEN_GOLEM],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 7,
      minimum: 50,
      frequency: 20,
      enemies: [
        EnemyType.BEE,
        EnemyType.MOUSEMAN2,
        EnemyType.GREEN_GOLEM,
        EnemyType.SPIDER,
      ],
      events: [],
    },
    {
      minute: 7.5,
      minimum: 70,
      frequency: 50,
      enemies: [
        EnemyType.BEE,
        EnemyType.MOUSEMAN2,
        EnemyType.GREEN_GOLEM,
        EnemyType.SPIDER,
      ],
      bosses: [],
      events: [],
    },
    {
      minute: 8,
      minimum: 40,
      frequency: 20,
      enemies: [EnemyType.BEE, EnemyType.GREEN_GOLEM],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 8.5,
      minimum: 25,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.GREEN_GOLEM],
      bosses: [],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 9,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.BEE, EnemyType.GREEN_GOLEM, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 9.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.BEE, EnemyType.GREEN_GOLEM, EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 10,
      minimum: 60,
      frequency: 500,
      enemies: [EnemyType.BEE, EnemyType.GREEN_GOLEM, EnemyType.SPIDER],
      bosses: [],
      events: [],
    },
    {
      minute: 10.5,
      minimum: 50,
      frequency: 100,
      enemies: [EnemyType.SKELETON, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 11,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.SKELETON, EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 11.5,
      minimum: 50,
      frequency: 50,
      enemies: [EnemyType.SKELETON, EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 12,
      minimum: 50,
      frequency: 20,
      enemies: [EnemyType.SKELETON, EnemyType.SPIDER, EnemyType.BEE],
      events: [],
    },
    {
      minute: 12.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.SKELETON, EnemyType.BEE],
      bosses: [],
      events: [],
    },
    {
      minute: 13,
      minimum: 40,
      frequency: 20,
      enemies: [EnemyType.SKELETON, EnemyType.BEE],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 13.5,
      minimum: 25,
      frequency: 50,
      enemies: [EnemyType.SKELETON, EnemyType.BEE],
      bosses: [],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 14,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.WOLF, EnemyType.BEE, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 14.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.WOLF, EnemyType.BEE, EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 15,
      minimum: 60,
      frequency: 500,
      enemies: [EnemyType.WOLF, EnemyType.BEE, EnemyType.SPIDER],
      bosses: [],
      events: [],
    },
    {
      minute: 15.5,
      minimum: 50,
      frequency: 100,
      enemies: [EnemyType.WOLF, EnemyType.BEE, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 16,
      minimum: 30,
      frequency: 50,
      enemies: [EnemyType.WOLF, EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 16.5,
      minimum: 50,
      frequency: 50,
      enemies: [EnemyType.WOLF, EnemyType.SPIDER],
      events: [
        {
          eventType: StageEventType.CHICKEN_SWARM,
          delay: 5000,
          repeat: 5,
          chance: 60,
        },
      ],
    },
    {
      minute: 17,
      minimum: 50,
      frequency: 20,
      enemies: [EnemyType.WOLF, EnemyType.SPIDER],
      events: [],
    },
    {
      minute: 17.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.WOLF, EnemyType.SKELETON],
      bosses: [],
      events: [],
    },
    {
      minute: 18,
      minimum: 40,
      frequency: 20,
      enemies: [EnemyType.WOLF, EnemyType.SKELETON],
      events: [
        {
          eventType: StageEventType.BEE_SWARM,
          delay: 1500,
          repeat: 15,
          chance: 70,
        },
      ],
    },
    {
      minute: 18.5,
      minimum: 25,
      frequency: 50,
      enemies: [EnemyType.WOLF, EnemyType.SKELETON],
      bosses: [],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 3000,
          repeat: 10,
          chance: 50,
        },
      ],
    },
    {
      minute: 19,
      minimum: 30,
      frequency: 100,
      enemies: [EnemyType.WOLF, EnemyType.SKELETON],
      events: [],
    },
    {
      minute: 19.5,
      minimum: 70,
      frequency: 50,
      enemies: [EnemyType.WOLF],
      events: [
        {
          eventType: StageEventType.SPIDER_SWARM,
          delay: 2000,
          repeat: 15,
          chance: 50,
        },
      ],
    },
    {
      minute: 20,
      minimum: 60,
      frequency: 500,
      enemies: [EnemyType.WOLF],
      bosses: [],
      events: [],
    },
    {
      minute: 21,
      minimum: 60,
      frequency: 500,
      enemies: [EnemyType.WOLF],
      bosses: [],
      events: [],
    },
    {
      minute: 22,
      minimum: 60,
      frequency: 500,
      enemies: [EnemyType.WOLF],
      bosses: [EnemyType.DEATH],
      events: [],
    },
  ],
};

export default STAGES_GREEN;
